import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, CheckCircle, Rocket } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { validatePassword } from "@/lib/validations";

const fleetBrand = {
  teal: "#4A9B8E",
  tealLight: "#5AADA0",
  aqua: "#82C5BB",
};

interface FleetTrialModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface TrialData {
  full_name: string;
  email: string;
  company_name: string;
  password: string;
}

export function FleetTrialModal({ open, onOpenChange }: FleetTrialModalProps) {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  

  const [data, setData] = useState<TrialData>({
    full_name: "",
    email: "",
    company_name: "",
    password: ""
  });

  const handleInputChange = (field: keyof TrialData, value: string) => {
    setData(prev => ({ ...prev, [field]: value }));
  };

  const isFormValid = () => {
    return data.full_name && data.email && data.company_name && validatePassword(data.password).isValid;
  };

  const handleSubmit = async () => {
    if (!isFormValid()) return;

    setLoading(true);
    try {
      const { data: result, error } = await supabase.functions.invoke('create-trial-company', {
        body: {
          email: data.email,
          password: data.password,
          full_name: data.full_name,
          company_name: data.company_name
        }
      });

      if (error) throw error;

      if (result?.success) {
        setSuccess(true);
        toast({
          title: "Conta trial criada!",
          description: result.message || "Sua conta foi criada com sucesso. Faça login para começar!",
        });

        // Reset form after 2 seconds and redirect to home
        setTimeout(() => {
          handleClose();
          navigate('/');
        }, 2000);
      } else {
        throw new Error(result?.error || 'Erro ao criar conta trial');
      }
    } catch (error: any) {
      console.error('Error creating trial:', error);
      toast({
        title: "Erro ao criar conta",
        description: error.message || "Ocorreu um erro ao criar sua conta trial. Tente novamente.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setData({ full_name: "", email: "", company_name: "", password: "" });
    setSuccess(false);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-2xl">
            <Rocket className="w-6 h-6" style={{ color: fleetBrand.teal }} />
            Teste Grátis 30 Dias - FleetONE
          </DialogTitle>
          <DialogDescription className="text-base">
            Teste todas as funcionalidades sem compromisso! Sem cartão de crédito. Cancele quando quiser.
          </DialogDescription>
        </DialogHeader>

        {success ? (
          <div className="flex flex-col items-center justify-center py-8 space-y-4">
            <CheckCircle className="w-16 h-16 text-success" />
            <h3 className="text-xl font-semibold">Conta criada com sucesso!</h3>
            <p className="text-muted-foreground text-center">
              Redirecionando...
            </p>
          </div>
        ) : (
          <div className="space-y-6 py-4">
            <div className="space-y-2">
              <Label htmlFor="full_name">Nome Completo *</Label>
              <Input
                id="full_name"
                type="text"
                value={data.full_name}
                onChange={(e) => handleInputChange('full_name', e.target.value)}
                placeholder="Seu nome completo"
                disabled={loading}
                required
                className="focus-visible:ring-[#4A9B8E] focus-visible:border-[#4A9B8E]"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={data.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                placeholder="seu@email.com"
                disabled={loading}
                required
                className="focus-visible:ring-[#4A9B8E] focus-visible:border-[#4A9B8E]"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="company_name">Nome da Empresa *</Label>
              <Input
                id="company_name"
                type="text"
                value={data.company_name}
                onChange={(e) => handleInputChange('company_name', e.target.value)}
                placeholder="Nome da sua empresa"
                disabled={loading}
                required
                className="focus-visible:ring-[#4A9B8E] focus-visible:border-[#4A9B8E]"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Senha *</Label>
              <Input
                id="password"
                type="password"
                value={data.password}
                onChange={(e) => handleInputChange('password', e.target.value)}
                placeholder="Mínimo 12 caracteres"
                disabled={loading}
                required
                minLength={12}
                className="focus-visible:ring-[#4A9B8E] focus-visible:border-[#4A9B8E]"
              />
              {data.password && !validatePassword(data.password).isValid && (
                <div className="text-sm text-destructive space-y-1">
                  {validatePassword(data.password).errors.map((error, idx) => (
                    <p key={idx}>• {error}</p>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-3 pt-4">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <CheckCircle className="w-4 h-4 text-success" />
                <span>Acesso completo por 30 dias</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <CheckCircle className="w-4 h-4 text-success" />
                <span>10 veículos inclusos</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <CheckCircle className="w-4 h-4 text-success" />
                <span>Suporte técnico</span>
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                onClick={handleClose}
                disabled={loading}
                className="flex-1"
              >
                Cancelar
              </Button>
              <button
                onClick={handleSubmit}
                disabled={!isFormValid() || loading}
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-md px-4 py-2 text-sm font-medium text-white transition-all disabled:opacity-50 disabled:pointer-events-none"
                style={{
                  background: (!isFormValid() || loading)
                    ? '#94a3b8'
                    : `linear-gradient(90deg, #4A9B8E, #82C5BB)`,
                  boxShadow: (!isFormValid() || loading)
                    ? 'none'
                    : '0 0 20px rgba(74, 155, 142, 0.3)',
                }}
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Criando...
                  </>
                ) : (
                  <>
                    <Rocket className="mr-2 h-4 w-4" />
                    Iniciar Teste Gratuito
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}